import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { UserPrivacyCurtain, UserSession } from "@repo/app-types/api/user";
import { LoadingState } from "@repo/app-types/client/client";
import type { Teammate } from "@repo/app-types/entities/teammate";
import type { RootState } from "../../app/store.js";
import { postCompanyData } from "../company/companySlice.js";

export interface TeammateState {
  teammate: Teammate | null;
  status: LoadingState;
  isUnauthorized: boolean | undefined;
  /** What the request is authenticated as, from `GET /user`. */
  session: UserSession | null;
  /** The employee privacy curtain, from `GET /user`; null when off. */
  privacyCurtain: UserPrivacyCurtain | null;
}
const initialState: TeammateState = {
  teammate: null,
  status: LoadingState.LOADING,
  isUnauthorized: undefined,
  session: null,
  privacyCurtain: null,
};

const teammateSlice = createSlice({
  name: "teammate",
  initialState,
  reducers: {
    setTeammate: (state, action: PayloadAction<Teammate>): TeammateState => {
      return {
        ...state,
        teammate: action.payload,
        status: LoadingState.LOADED,
        isUnauthorized: false,
      };
    },
    setUserSession: (
      state,
      action: PayloadAction<{
        session: UserSession;
        privacyCurtain: UserPrivacyCurtain | null;
      }>,
    ) => {
      state.session = action.payload.session;
      state.privacyCurtain = action.payload.privacyCurtain;
    },
    bootFailed: (state, action: PayloadAction<{ isUnauthorized: boolean }>) => {
      if (!state.teammate) {
        state.status = LoadingState.ERROR;
        state.isUnauthorized = action.payload.isUnauthorized;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postCompanyData.fulfilled, (state, action) => {
      // Update the teammate's company_id when we create a new company for a new user
      if (state.teammate) state.teammate.company_id = action.payload.id;
    });
  },
});

export const { setTeammate, setUserSession, bootFailed } =
  teammateSlice.actions;
export default teammateSlice.reducer;

export const selectTeammateLoadingStatus = (state: RootState) =>
  state.teammate.status;
export const selectTeammate = (state: RootState) => state.teammate.teammate;

export const selectUserSession = (state: RootState) => state.teammate.session;

export const selectPrivacyCurtain = (state: RootState) =>
  state.teammate.privacyCurtain;

/** Whether an Alpharun employee is here through service access. */
export const selectIsServiceSession = (state: RootState) =>
  state.teammate.session?.type === "service";
